/**
 * ShareButton — viral share pack for fair-launch markets (Gate 8).
 *
 * Renders a "Share" button that:
 *  1. Copies the market URL (with optional ?ref=<walletAddress>) to clipboard.
 *  2. Opens a pre-filled Twitter/X share intent.
 *  3. Shows a small popover with both actions.
 *
 * Usage:
 *   <ShareButton
 *     tokenName="WAGMI"
 *     symbol="WAGMI"
 *     marketUrl="https://synapz.app/fair-launch/market/46630/0xabc…"
 *     referrerAddress="0x1234…"   // optional — connected wallet
 *   />
 */

import { useState, useRef, useEffect } from "react";

interface ShareButtonProps {
  tokenName: string;
  symbol: string;
  marketUrl: string;
  referrerAddress?: string;
}

export function ShareButton({ tokenName: _tokenName, symbol, marketUrl, referrerAddress }: ShareButtonProps) {
  const [open, setOpen]       = useState(false);
  const [copied, setCopied]   = useState(false);
  const containerRef          = useRef<HTMLDivElement>(null);

  // Build the share URL — add ?ref=<wallet> when connected.
  const shareUrl = (() => {
    try {
      const u = new URL(marketUrl);
      if (referrerAddress) u.searchParams.set("ref", referrerAddress);
      return u.toString();
    } catch {
      return marketUrl;
    }
  })();

  const tweetText = encodeURIComponent(
    `Trading $${symbol} on SYNAPZ — fair-launch token launchpad on Robinhood Chain 🚀\n${shareUrl}`,
  );
  const tweetUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;

  // Close popover on outside click.
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select a hidden input.
      const input = document.createElement("input");
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-xs text-muted hover:text-ink transition-colors"
        aria-label="Share market"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 1 1 0-5.368m0 5.368A3 3 0 0 0 12 15a3 3 0 0 1 0 5.368M12 15a3 3 0 0 0 3.316-1.342M12 9a3 3 0 0 1 3.316 1.342m0 0a3 3 0 1 1 0 5.316" />
        </svg>
        Share
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 w-52 rounded-xl bg-panel border border-line/40 shadow-2xl p-2 space-y-1">
          {/* Copy link */}
          <button
            onClick={copyLink}
            className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-ink transition-colors"
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 text-plasma shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
                </svg>
                <span className="text-plasma">Copied!</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M8 16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2m-6 12h8a2 2 0 0 1 2-2v-8a2 2 0 0 1-2-2h-8a2 2 0 0 1-2 2v8a2 2 0 0 1 2 2Z" />
                </svg>
                <span>Copy link{referrerAddress ? " (with ref)" : ""}</span>
              </>
            )}
          </button>

          {/* Share on X */}
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-ink transition-colors"
            onClick={() => setOpen(false)}
          >
            <svg className="w-4 h-4 text-muted shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.632 5.905-5.632Zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            <span>Share on X</span>
          </a>

          {referrerAddress && (
            <p className="px-3 py-1 text-[10px] text-muted/50 leading-tight">
              Your wallet is attached as referrer. You may earn rewards when referrals trade.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
